/**
 * Backwards-compatible shim around the analytics bus.
 *
 * NEW CODE should import from `@/lib/analytics` directly:
 *   import { track, trackScreen, identify, reset } from '@/lib/analytics';
 *
 * This file remains so existing call sites continue to work, and so
 * `initPostHog()` from main.tsx still does the right thing (now: init the bus
 * with all destinations, including PostHog).
 *
 * Global error/unhandledrejection listeners that previously lived here are
 * now wired through the bus so they reach every destination.
 */
import {
  initAnalytics,
  track,
  trackScreen as busTrackScreen,
  identify as busIdentify,
  reset as busReset,
} from './analytics';
import type { EventName, EventProps } from './analytics';

let globalListenersAttached = false;

const attachGlobalErrorListeners = () => {
  if (globalListenersAttached || typeof window === 'undefined') return;
  globalListenersAttached = true;
  window.addEventListener('error', (event) => {
    track('$exception' as EventName, {
      $exception_type: 'Error',
      $exception_message: event.message,
      $exception_source: event.filename,
      $exception_lineno: event.lineno,
      $exception_colno: event.colno,
      $exception_stack: event.error?.stack,
    });
  });
  window.addEventListener('unhandledrejection', (event) => {
    track('$exception' as EventName, {
      $exception_type: 'UnhandledRejection',
      $exception_message: event.reason?.message || String(event.reason),
      $exception_stack: event.reason?.stack,
    });
  });
};

export const initPostHog = async () => {
  await initAnalytics();
  attachGlobalErrorListeners();
};

export const captureEvent = (name: string, properties?: EventProps) => {
  track(name as EventName, properties || {});
};

export const trackScreen = (name: string, properties?: EventProps) => {
  busTrackScreen(name, properties || {});
};

export const identifyUser = (id: string, properties?: EventProps) => {
  busIdentify(id, properties);
};

export const resetUser = () => {
  busReset();
};

// Default export retained for any rare direct import; prefer the bus API.
export default { captureEvent, trackScreen, identifyUser, resetUser };
